import { EventType } from "@/analytics/analytics-events";
import { useMobileViewerUi } from "@/components/common/mobile-viewer-ui-context";
import { usePhoneViewerLayout } from "@/hooks/use-phone-viewer-layout";
import { createPortal } from "react-dom";
import {
  SelectCaptureProperties,
  SelectCaptureSources,
} from "@/analytics/generic-events-properties";
import { SceneFilterToggle } from "@/components/common/scene-filter-toggle";
import { AvailableAnnotationTools } from "@/components/r3f/renderers/annotations/annotations-types";
import {
  DataSessionsDropDown,
  findSelectedPhotosGroup,
} from "@/components/ui/data-sessions-dropdown";
import { ToolsHelpBanners } from "@/components/ui/help-banners/tools-help-banners";
import { SplitScreenButton } from "@/components/ui/splitscreen-button";
import { useCurrentArea } from "@/modes/mode-data-context";
import { splitMode } from "@/modes/split-mode/split-mode";
import {
  FeaturesState,
  selectFeaturesSlice,
} from "@/store/features/features-slice";
import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import { changeMode } from "@/store/mode-slice";
import { setCompareSceneFilter } from "@/store/modes/walk-mode-slice";
import { curryAppSelector } from "@/store/reselect";
import { selectActiveElement } from "@/store/selections-selectors";
import { setActiveElement } from "@/store/selections-slice";
import { RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  selectCanReadPointCloud,
  selectSubscriptionFeaturesSlice,
} from "@/store/subscription-features/subscription-features-selectors";
import { SubscriptionFeaturesState } from "@/store/subscription-features/subscription-features-slice";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { setIsProjectOverviewExpanded, ToolName } from "@/store/ui/ui-slice";
import { ManualBlurringToolbar } from "@/tools/manual-blurring-tool/manual-blurring-toolbar";
import { SceneFilter } from "@/types/scene-filter";
import {
  IElementsState,
  selectAncestor,
  selectChildDepthFirst,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, includes } from "@faro-lotv/foundation";
import {
  compareById,
  GUID,
  IElement,
  IElementImg360,
  IElementModel3dStream,
  IElementSection,
  IElementTypeHint,
  isIElementImg2d,
} from "@faro-lotv/ielement-types";
import { Stack, SxProps, Theme } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import { useCallback, useMemo, type JSX } from "react";
import { PhotoPreviewList, usePhotoGallery } from "./photo-preview-list";
import { useIsPanoWithDepthAndReady } from "./walk-mode-hooks";
import {
  TypeToggleButtonsLayout,
  useWalkOverlayResponsiveLayout,
} from "./walk-overlay-responsive-layout";
import {
  isElementPhotosGroup,
  selectWalkOverlayDropdownOptions,
} from "./walk-overlay-selectors";
import { WalkSceneActiveElement } from "./walk-types";

type WalkOverlayProps = {
  /** Current main element the user is navigating in walk mode */
  activeWalkElement: WalkSceneActiveElement;

  /** The current reference element for the active element (Eg. DataSession, VideoRecording, Room) */
  referenceElement?: IElementSection;

  /** True if there are panos and/or photos available in the current scene */
  hasPanosOrPhotos: boolean;

  /** The current cad model */
  cad?: IElementModel3dStream;

  /**
   * If `true`, the user is currently using the measurement tool.
   *
   * The Overlay will add a help banner indicating that measurements are
   * not possible in 360 images when in panorama mode.
   */
  isMeasuring: boolean;

  /** The current filtering of the scene of scene */
  sceneFilter: SceneFilter;

  /** Call back for when the scene filter is changed */
  onSceneFilterChanged(sceneFilter: SceneFilter): void;

  /** Call back for when the active element is changed */
  onActiveElementChanged(id: GUID): void;

  /** Call back for when the target 2D photo object is changed */
  onPhotoObjectChanged(id: GUID): void;

  /** Function called when the pano type selected in the menu changed */
  onPanoTypeChanged?(showIntensity: boolean, siblingPano: IElementImg360): void;

  /**
   * False if the measure help banner doesn't need to be handled in walk overlay
   *
   * @default true
   */
  shouldHandleMeasureHelpBanner?: boolean;

  /** Additional styles */
  sx?: SxProps<Theme>;
};

/**
 * @returns renderer for the walk mode overlay
 */
export function WalkOverlay({
  activeWalkElement,
  referenceElement,
  hasPanosOrPhotos,
  cad,
  isMeasuring,
  sceneFilter,
  onSceneFilterChanged,
  onActiveElementChanged,
  onPanoTypeChanged,
  onPhotoObjectChanged,
  shouldHandleMeasureHelpBanner = true,
  sx,
}: WalkOverlayProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const isPhone = usePhoneViewerLayout();
  const mobileUi = useMobileViewerUi();

  const currentArea = useCurrentArea();

  const { isPanoWithDepth, isDepthReady } =
    useIsPanoWithDepthAndReady(activeWalkElement);

  const { buttonLayout, overlayRef } = useWalkOverlayResponsiveLayout();

  const { dataSessions2d, dataSessions3d, dataSessions5d, roomsSections } =
    useCurrentArea();

  const referenceRoom = useAppSelector(
    selectAncestor(activeWalkElement, (el) =>
      includes(roomsSections, el, compareById),
    ),
  );
  const options = useAppSelector(
    selectWalkOverlayDropdownOptions(
      roomsSections,
      dataSessions2d,
      dataSessions3d,
      dataSessions5d,
      activeWalkElement,
      sceneFilter,
    ),
    isEqual,
  );

  const hasPcReadSupport = useAppSelector(selectCanReadPointCloud);

  const canEnablePointCloud =
    hasPcReadSupport &&
    (currentArea.dataSessions3d.pointClouds.length > 0 ||
      currentArea.dataSessions5d.length > 0);

  const canEnableCad = !!cad;

  const hasGaussianSplats = !!currentArea.dataSessions3d.gaussianSplats.length;

  const isOverlayEnabled = canEnableCad && canEnablePointCloud;

  function determineDisabledTextForOverlayButton(): string | undefined {
    if (isOverlayEnabled) return;
    return "Overlay feature unavailable: missing required data.";
  }

  // Depths are only needed to measure on a pano, so measuring the point cloud or CAD is unaffected
  const cannotMeasureBecauseWaitingForDepths = useMemo(
    () =>
      sceneFilter === SceneFilter.Pano &&
      isPanoWithDepth &&
      !isDepthReady &&
      isMeasuring,
    [sceneFilter, isDepthReady, isMeasuring, isPanoWithDepth],
  );

  // Determine the available variants of annotation tool for the current scene
  const availableAnnotationTool = useMemo(() => {
    if (
      sceneFilter === SceneFilter.Pano &&
      activeWalkElement.typeHint !== IElementTypeHint.odometryPath
    ) {
      return isPanoWithDepth && !isDepthReady
        ? AvailableAnnotationTools.rectangle
        : AvailableAnnotationTools.all;
    }
  }, [activeWalkElement, isPanoWithDepth, isDepthReady, sceneFilter]);

  const selectedPhotoGroup = useMemo(
    () =>
      isIElementImg2d(activeWalkElement)
        ? findSelectedPhotosGroup(options, activeWalkElement)
        : undefined,
    [activeWalkElement, options],
  );
  const photoIndex = useMemo(
    () =>
      selectedPhotoGroup
        ? selectedPhotoGroup.photos.findIndex(
            (element) => element.id === activeWalkElement.id,
          )
        : undefined,
    [activeWalkElement, selectedPhotoGroup],
  );

  const photoGallery = usePhotoGallery(
    options,
    activeWalkElement,
    onActiveElementChanged,
    onPhotoObjectChanged,
  );

  const { getState } = useAppStore();
  const handleElementDeletion = useCallback(
    (element: IElement) => {
      // First, find the selected option and its index from the option list
      const currentOption = isIElementImg2d(element)
        ? findSelectedPhotosGroup(options, element)
        : element;
      assert(currentOption, "Current selection must exist");
      const currentIndex = options.findIndex((e) => e.id === currentOption.id);

      // Second, find the new active element
      let activeElement;
      if (
        isElementPhotosGroup(currentOption) &&
        currentOption.photos.length > 1
      ) {
        // When deleting a 2D photo, if the photo group contains more than one photos,
        // find the closest photo from remaining photos
        const photoIndex = currentOption.photos.findIndex(
          (photo) => photo.id === element.id,
        );
        activeElement =
          photoIndex > 0
            ? currentOption.photos[photoIndex - 1]
            : currentOption.photos[photoIndex + 1];
      } else if (options.length > 1) {
        // Find the closest option from remaining options
        activeElement =
          currentIndex > 0
            ? options[currentIndex - 1]
            : options[currentIndex + 1];
      }

      if (activeElement) {
        const state = getState();
        const mainElement = selectActiveElement(state);

        const id = isElementPhotosGroup(activeElement)
          ? activeElement.photos[0].id
          : activeElement.id;
        onActiveElementChanged(id);

        // If deleting an element from the compare scene in split scene and it's also
        // the active one in the main scene, we should change the active element too
        if (
          state.mode.name === "split" &&
          selectChildDepthFirst(element, (e) => e.id === mainElement?.id)(state)
        ) {
          dispatch(setActiveElement(id));
        }

        // stay in walk mode with the new active element
        return true;
      }

      // will switch to 2D overview
      return false;
    },
    [dispatch, getState, onActiveElementChanged, options],
  );

  const controls = (
    <Stack direction="column" spacing={-0.5} sx={sx}>
      <Stack
        direction={
          isPhone || buttonLayout === TypeToggleButtonsLayout.Small
            ? "column"
            : "row"
        }
        spacing={1}
        sx={{ justifyContent: options.length ? "space-between" : "end" }}
        ref={overlayRef}
      >
        <Stack
          sx={{
            gap: 1.25,
            overflow: "hidden",
          }}
        >
          {!!options.length && (
            <DataSessionsDropDown
              activeElement={activeWalkElement}
              referenceElement={referenceRoom ?? referenceElement}
              datasets={options}
              photoGallery={photoGallery}
              onDataSessionChanged={(id) => {
                if (isPhone) mobileUi?.setSurface(null);
                if (sceneFilter === SceneFilter.Pano) {
                  const newSelection = options.find((el) => el.id === id);
                  assert(newSelection, "New select must exist.");
                  Analytics.track<SelectCaptureProperties>(
                    EventType.selectCapture,
                    {
                      via: SelectCaptureSources.dropdown,
                      type: isElementPhotosGroup(newSelection) ? "2D" : "360",
                    },
                  );
                  onActiveElementChanged(
                    isElementPhotosGroup(newSelection)
                      ? newSelection.photos[0].id
                      : newSelection.id,
                  );
                  return;
                }
                onActiveElementChanged(id);
              }}
              handleElementDeletion={handleElementDeletion}
            />
          )}
        </Stack>
        <SceneFilterToggle
          value={sceneFilter}
          onValueChanged={onSceneFilterChanged}
          onActiveElementChanged={onActiveElementChanged}
          onPanoTypeChanged={onPanoTypeChanged}
          currentMainElement={activeWalkElement}
          panoVisible={hasPanosOrPhotos}
          pointCloudEnabled={canEnablePointCloud}
          cadEnabled={canEnableCad}
          gaussianSplatsEnabled={hasGaussianSplats}
          overlayEnabled={isOverlayEnabled}
          overlayDisabledText={determineDisabledTextForOverlayButton()}
          layout={buttonLayout}
        />
      </Stack>

      {selectedPhotoGroup && selectedPhotoGroup.photos.length > 1 && (
        <PhotoPreviewList
          photos={selectedPhotoGroup.photos}
          selected={photoIndex ?? 0}
          onPhotoSelected={onActiveElementChanged}
        />
      )}
    </Stack>
  );

  const mobileControls = mobileUi?.viewContainer
    ? createPortal(controls, mobileUi.viewContainer)
    : null;
  return (
    <>
      {isPhone ? mobileControls : controls}
      {!isPhone && (
        <ToolsHelpBanners
          measureHelpBanner={{
            enabled:
              shouldHandleMeasureHelpBanner &&
              !cannotMeasureBecauseWaitingForDepths,
            cannotMeasureBecauseWaitingForDepths,
          }}
          annotationHelpBanner={{ enabled: true, availableAnnotationTool }}
        />
      )}
    </>
  );
}

type WalkOverlayBottomButtonsProps = {
  /** Reference to the currently selected pano or point cloud element */
  activeElement?: WalkSceneActiveElement;

  /** Current scene type */
  walkSceneFilter: SceneFilter;
};

/**
 * @returns the buttons at the bottom of the WalkOverlay to enable the split screen
 */
export function WalkOverlayBottomButtons({
  activeElement,
  walkSceneFilter,
}: WalkOverlayBottomButtonsProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const isPhone = usePhoneViewerLayout();

  const activeTool = useAppSelector(selectActiveTool);

  // Find the rightCandidateSection and if it doesn't exist, then disable the splitscreen
  const canStartSplitScreen =
    useAppSelector(selectCanStartSplitScreen(activeElement)) &&
    walkSceneFilter !== SceneFilter.Overlay;

  const validCompareFilters = [SceneFilter.Pano, SceneFilter.PointCloud];

  if (activeTool === ToolName.manualBlurring) {
    return <ManualBlurringToolbar />;
  }

  if (isPhone) return null;

  return (
    <SplitScreenButton
      enabled={canStartSplitScreen}
      checked={false}
      onClick={() => {
        Analytics.track(EventType.turnSplitscreenOn);
        dispatch(changeMode("split"));
        dispatch(setIsProjectOverviewExpanded(false));
        dispatch(
          setCompareSceneFilter(
            validCompareFilters.includes(walkSceneFilter)
              ? walkSceneFilter
              : SceneFilter.Pano,
          ),
        );
      }}
    />
  );
}

/**
 * This selector is worth memoizing because the call to mode.canBeStartedWith is expensive
 *
 * @param state the root application state
 * @param activeElement the active element to check if the split screen can be started with
 * @returns true if the split screen can be started with the active element
 */
const selectCanStartSplitScreen = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectSubscriptionFeaturesSlice,
      selectFeaturesSlice,
      (state: RootState, activeElement: WalkSceneActiveElement | undefined) =>
        activeElement,
    ],
    computeCanStartSplitScreen,
  ),
);

function computeCanStartSplitScreen(
  iElements: IElementsState,
  subscriptionFeatures: SubscriptionFeaturesState,
  features: FeaturesState,
  activeElement: WalkSceneActiveElement | undefined,
): boolean {
  return !!(
    activeElement &&
    splitMode.canBeStartedWith?.(activeElement, {
      iElements,
      subscriptionFeatures,
      features,
    })
  );
}
